import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { onAuthChange, signOutCurrentUser } from './lib/firebase';
import { fetchMe, type MeResponse } from './lib/api';
import SignIn from './pages/SignIn';
import Home from './pages/Home';
import LinkDetail from './pages/LinkDetail';
import NewLink from './pages/NewLink';
import Admin from './pages/Admin';
import Restore from './pages/Restore';

interface AuthState {
  loading: boolean;
  signedIn: boolean;
  me: MeResponse | null;
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ loading: true, signedIn: false, me: null });

  useEffect(() => {
    return onAuthChange(async (user) => {
      if (!user) {
        setAuth({ loading: false, signedIn: false, me: null });
        return;
      }
      try {
        const me = await fetchMe();
        setAuth({ loading: false, signedIn: true, me });
      } catch (e) {
        console.error('fetchMe failed', e);
        setAuth({ loading: false, signedIn: true, me: null });
      }
    });
  }, []);

  // /restore/<slug> is the App Link install fallback — must render without auth
  // since users hitting it are uninstalled (or not signed in).
  const path = window.location.pathname;
  if (path.startsWith('/restore/')) {
    return (
      <Routes>
        <Route path="/restore/:slug" element={<Restore />} />
      </Routes>
    );
  }

  if (auth.loading) {
    return <div className="flex items-center justify-center h-full text-gray-500">Loading…</div>;
  }
  if (!auth.signedIn) {
    return <SignIn />;
  }

  return (
    <div className="min-h-full flex flex-col bg-gray-50">
      <Nav me={auth.me} />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8">
        <Routes>
          <Route path="/" element={<Home me={auth.me} />} />
          <Route path="/new" element={<NewLink />} />
          <Route path="/links/:id" element={<LinkDetail me={auth.me} />} />
          {auth.me?.is_admin && <Route path="/admin" element={<Admin />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Nav({ me }: { me: MeResponse | null }) {
  const loc = useLocation();
  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="font-semibold text-gray-900">Linkr</Link>
        <div className="flex items-center gap-4 text-sm">
          {me?.is_admin && (
            <>
              <Link to="/admin" className="text-purple-700 hover:underline">Users</Link>
              <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium">admin</span>
            </>
          )}
          <span className="text-gray-600">{me?.email}</span>
          <button
            onClick={() => signOutCurrentUser()}
            className="text-gray-500 hover:text-gray-900"
          >
            Sign out
          </button>
        </div>
      </div>
      <div className="hidden">{loc.pathname}</div>
    </header>
  );
}
